import { gsap } from 'gsap/gsap-core';
import * as THREE from 'three';

import ResourceApi from '../../../Api/SiteApi/ResourceApi';

import { ViewerContainer } from './ViewerContainer';
import { DRACOLoader } from './core/DRACOLoader';
import { EffectComposer } from './core/EffectComposer';
import { FXAAShader } from './core/FXAAShader';
import { GLTFLoader } from './core/GLTFLoader';
import { GammaCorrectionShader } from './core/GammaCorrectionShader';
import { OrbitControls } from './core/OrbitControls';
import { RGBELoader } from './core/RGBELoader';
import { RenderPass } from './core/RenderPass';
import { ShaderPass } from './core/ShaderPass';
import StatsModule from './core/StatsModule';

declare global {
    interface Window {
        viewer?: any;
    }
}

export class ModelViewer {
    private debug = true;
    private models = [];
    private textures = new Map();
    private gltfs = new Map();
    private envMap = null;
    private envMapIntensity = 0;
    private envMapRenderTarget = null;
    private lookAtVector3 = new THREE.Vector3(0, 1.2, 0);
    private readyTime = 0;
    private pathToAssets = '/images/base/draw-animation';
    private container: ViewerContainer;
    private clock: THREE.Clock;
    private loadingManager: THREE.LoadingManager;
    private textureLoader: THREE.TextureLoader;
    private gltfLoader: GLTFLoader;
    private dracoLoader: DRACOLoader;
    private renderer: THREE.WebGLRenderer;
    private scene: THREE.Scene;
    private camera: THREE.PerspectiveCamera;
    private controls: OrbitControls;
    private stats: any;
    private effectComposer: any;

    run = () => { };

    constructor(debug, container) {
        this.container = container;
        this.container.resetLoader();

        this.debug = debug;
        this.clock = new THREE.Clock();
        this.loadingManager = new THREE.LoadingManager(
            () => {
                setTimeout(() => {
                    this.log('viewer running...');
                    this.run();

                    setTimeout(() => {
                        this.container.hideLoader();
                    }, 100);

                    gsap.to(this.scene.getObjectByName('intro').material.uniforms.uAlpha, {
                        duration: 0,
                        value: 0,
                        onComplete: () => {
                            this.readyTime = this.getCurrentTime();
                        },
                    });
                }, 50);
            },
            (url, loaded, total) => {
                this.log(`loading ${loaded}/${total}: ${url}`);
                this.container.setLoader((loaded / total) * 100);
            },
            (url) => {
                this.log(`Error: ${url}`);
            }
        );
        this.textureLoader = new THREE.TextureLoader(this.loadingManager);

        this.dracoLoader = new DRACOLoader(this.loadingManager);
        this.dracoLoader.setDecoderPath(`${this.getPathToAssets()}/draco/`);
        this.gltfLoader = new GLTFLoader(this.loadingManager);
        this.gltfLoader.setDRACOLoader(this.dracoLoader);

        this.init();
        this.mainLoop();
    }

    static create(debug) {
        ModelViewer.dispose();

        const container = new ViewerContainer(
            '#app-3d-canvas',
            1000,
            640,
            document.getElementById('app-3d-container'),
            document.getElementById('app-3d-output'),
            document.getElementById('app-3d-loader')
        );

        return new ModelViewer(debug, container);
    }

    static dispose() {
        if (window.viewer) {
            window.viewer.dispose();
            window.viewer = null;
        }
    }

    init() {
        this.initRenderer();
        this.initScene();
        this.initCamera();
        if (this.debug) this.initControls();

        // this.initPostProcessing();
        this.initResize();
    }

    view(run: () => void) {
        this.run = run;
    }

    getPathToAssets() {
        return this.pathToAssets;
    }

    getReadyTime() {
        return this.readyTime;
    }

    loadSRGBTexture(name: string, path: string, defaultPath = null) {
        ResourceApi.getImageByTemplate(path, defaultPath).then((response) => {
            const texture = this.textureLoader.load(response);
            texture.encoding = THREE.sRGBEncoding;
            texture.flipY = false;
            this.textures.set(name, texture);
        });
    }

    loadLinearTexture(name: string, url: string) {
        const texture = this.textureLoader.load(url);
        texture.flipY = false;
        this.textures.set(name, texture);
    }

    getTexture(name: string) {
        if (this.textures.has(name)) {
            return this.textures.get(name);
        }
        throw new Error(`No texture with name: ${name}`);
    }

    addModel(model) {
        model.init();
        this.models.push(model);
    }

    loadHDRI(path: string, envMapIntensity: number) {
        const pmremGenerator = new THREE.PMREMGenerator(this.renderer);
        pmremGenerator.compileEquirectangularShader();

        new RGBELoader(this.loadingManager).setDataType(THREE.FloatType).load(path, (envTexture) => {
            envTexture.mapping = THREE.EquirectangularReflectionMapping;
            this.envMapRenderTarget = pmremGenerator.fromEquirectangular(envTexture);

            this.envMap = this.envMapRenderTarget.texture;
            this.envMap.encoding = THREE.sRGBEncoding;
            this.envMapIntensity = envMapIntensity;

            envTexture.dispose();
            pmremGenerator.dispose();
        });
    }

    getEnvMap() {
        return this.envMap;
    }

    getEnvMapIntensity() {
        return this.envMapIntensity;
    }

    /**
     * @param name
     * @param path
     */
    loadGLTF(name: string, path: string) {
        this.gltfLoader.load(path, (gltf) => {
            this.gltfs.set(name, gltf);
        });
    }

    getGLTF(name: string) {
        if (this.gltfs.has(name)) {
            return this.gltfs.get(name);
        }
        throw new Error(`No model with name: ${name}`);
    }

    initScene() {
        this.scene = new THREE.Scene();
        this.scene.background = 0x000000;
        // stats
        this.stats = StatsModule();

        if (this.debug) {
            this.container.appendChild(this.stats.dom);
            const gridHelper = new THREE.GridHelper(4, 4);
            this.scene.add(gridHelper);
            const axesHelper = new THREE.AxesHelper(4);
            this.scene.add(axesHelper);
        }
    }

    initCamera() {
        this.camera = new THREE.PerspectiveCamera(
            28,
            this.container.getClientWidth() / this.container.getHeight(),
            0.1,
            100
        );

        this.camera.position.set(0, 2, 6.2);
        // doesn't work when controls enabled
        this.camera.lookAt(this.lookAtVector3);

        this.resize();
    }

    initControls() {
        this.controls = new OrbitControls(this.camera, this.renderer.domElement);
        this.controls.target = this.lookAtVector3;
        this.controls.update();
    }

    /**
     * canvas
     */
    initRenderer() {
        this.renderer = new THREE.WebGLRenderer({
            canvas: this.container.getCanvas(),
            antialias: true,
        });
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.renderer.setSize(this.container.getClientWidth(), this.container.getClientHeight());

        this.renderer.outputEncoding = THREE.sRGBEncoding;
        this.renderer.useLegacyLights = true;
        this.renderer.shadowMap.enabled = true;
        this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    }

    initPostProcessing() {
        // post processing
        const renderTarget = new THREE.WebGLRenderTarget(800, 600, {
            minFilter: THREE.LinearFilter,
            magFilter: THREE.LinearFilter,
            format: THREE.RGBAFormat,
            encoding: THREE.sRGBEncoding,
        });
        renderTarget.texture.encoding = THREE.sRGBEncoding;

        this.effectComposer = new EffectComposer(this.renderer, renderTarget);
        this.effectComposer.setPixelRatio(window.devicePixelRatio);
        this.effectComposer.setSize(this.container.getClientWidth(), this.container.getClientHeight());

        const renderPass = new RenderPass(this.scene, this.camera);

        this.effectComposer.addPass(renderPass);
        this.effectComposer.addPass(new ShaderPass(FXAAShader));
        this.effectComposer.addPass(new ShaderPass(GammaCorrectionShader));
    }

    resize() {
        this.container.updateContainerHeight();

        this.camera.aspect = this.container.getClientWidth() / this.container.getClientHeight();
        this.camera.updateProjectionMatrix();

        this.renderer.setPixelRatio(window.devicePixelRatio);

        this.renderer.setSize(this.container.getClientWidth(), this.container.getClientHeight());

        if (this.effectComposer) {
            this.effectComposer.setPixelRatio(window.devicePixelRatio);
            this.effectComposer.setSize(this.container.getClientWidth(), this.container.getClientHeight());
        }

        this.render();
    }

    initResize() {
        /*
         * https://discourse.threejs.org/t/keeping-an-object-scaled-based-on-the-bounds-of-the-canvas-really-battling-to-explain-this-one/17574/10
         */
        window.addEventListener('resize', () => this.resize(), false);
    }

    dispose() {
        this.textures.forEach((texture, key) => {
            texture.dispose();
            this.textures.delete(key);
        });

        this.textures.clear();

        this.models.forEach((model) => {
            model.dispose();
        });

        do {
            this.models.splice(0, 1);
        } while (this.models.length > 0);
        this.models = [];

        this.gltfs.forEach((gltf, key) => {
            this.gltfs.delete(key);
        });

        this.gltfs.clear();

        this.controls?.dispose();

        this.envMapRenderTarget?.dispose();
        this.envMapRenderTarget = null;

        this.envMap?.dispose();
        this.envMap = null;

        this.dracoLoader.dispose();
        this.dracoLoader = null;

        this.renderer.renderLists.dispose();
        this.renderer.dispose();
    }

    mainLoop() {
        this.container.clearOutputContent();
        const delta = this.clock.getDelta();

        if (this.debug) {
            this.container.appendOutputTime(this.getCurrentTime());
            this.container.appendDebugOutput(this.models, this.gltfs, this.renderer);
        }

        // this.worldCenterAnimation();
        requestAnimationFrame(() => {
            this.mainLoop();
        });

        this.models.forEach((model) => {
            model.animate(delta);
        });

        if (this.debug) {
            this.stats.update();
            this.controls.update();
        }

        this.render();
        this.container.updateOutput();
    }

    render() {
        this.renderer.render(this.scene, this.camera);
    }

    getCurrentTime() {
        return this.clock.elapsedTime;
    }

    log(message: string) {
        if (this.debug) {
            console.log(message);
        }
    }

    sleep(ms: number) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }
}
